import { Divider, Drawer, Image } from "antd";
import Heading from "../../components/Heading";
import { Fragment } from "react";
import SideDrawer from "./SideDrawer";

interface ChangelogDrawerProps {
    visible?: boolean;
    onClose?: () => void;
}

const ChangelogDrawer = ({
    visible,
    onClose
}: ChangelogDrawerProps) => {
    const changelog = [
        {
            title: "⚡ Assign a custom domain on Applications and Resources",
            date: "Jan 13, 2023",
            description: <>
                With this update we are dramatically simplifying the custom domain setup for your applications and cloud resources.
                <br />
                <br />
                To assign a custom domain just go to your application or resource details and click on <b>Assign domain</b> button and then just follow the few simple steps.
                <br />
                <br />
                For detailed instructions follow the <a href="https://docs.microtica.com/assign-a-custom-domain" target="_blank">Assign a Custom Domain</a> docs.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/Ug59-RXL_mlI2PwwnMFkx_image.png?auto=format&ixlib=react-9.1.1&w=1153&h=517&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#Q2F6C"
        },
        {
            title: "⚡ Pipeline builds for your applications",
            date: "Dec 2, 2022",
            description: <>
                We are integrating application pipeline build and deployment executions in the Application Pipeline section.
                <br />
                <br />
                Now you will be able to navigate through pipeline execution history and go to a specific execution to see the logs and debug any potential issues with the application build or deployment process.
                <br />
                <br />
                Happy deploying! 🚀
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/929d4SKzVCLMiUJJHQnlc_image.png?auto=format&ixlib=react-9.1.1&w=2292&h=1406&dpr=2&q=50",
            link: "https://docs.microtica.com/changelog#VdJS9"
        },
        {
            title: "⚡ Resource Logs - follow your application logs directly from Microtica Portal",
            date: "Nov 18, 2022",
            description: <>
                Up until now when you had to debug an application deployed on AWS Fargate (Strapi, Medusa etc.) you had to login into AWS console and follow the logs from there.
                <br />
                <br />
                This was a big pain point for our customers so today we are happy to announce that we are releasing the integrated resource logs into our portal. With this change you no longer have to login into AWS console to debug your apps.
                <br />
                <br />
                Happy debugging! 🪲
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/weyXdide0QhT60LyZm3M7_screenshot-2022-11-18-at-100423.png?auto=format&ixlib=react-9.1.1&w=1135&h=568&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#5AHs3"
        },
        {
            title: "📢 Introducing Medusa Server Template",
            date: "Nov 11, 2022",
            description: <>
                You can now deploy a production-ready Medusa Server on your AWS account in a few simple steps.
                <br />
                <br />
                The template includes managed Postgres and Redis instances for high availability and scalability. Alternatively, for those who want a cheaper solution for development, the template offers a "development" mode that works with SQLite and "fake" Redis instance.
                <br />
                <br />
                The solution can be fully integrated with your existing Medusa Admin and Medusa Storefront.
                <br />
                <br />
                In the upcoming months we plan to release a bundle of all three Medusa components as one solution.
                <br />
                <br />
                Happy deploying! 🚀
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/Lbi3R1CEsgSpZOuNqU_sO_twitter-3.png?auto=format&ixlib=react-9.1.1&w=2400&h=1350&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#QCDpk"
        },
        {
            title: "🪲 Improvements and fixes",
            date: "Nov 4, 2022",
            description: <ul>
                <li>Improved navigation across the whole application with more accessible contextual actions</li>
                <li>Application monitoring and logs are now the default view in application details</li>
                <li>Improved the app list by providing more visibility of running, pending and failed pods</li>
            </ul>,
            link: "https://docs.microtica.com/changelog#bzFhx"
        },
        {
            title: "📢 Introducing Template Executions",
            date: "Oct 14, 2022",
            description: <>
                You can now follow template deployment executions in real-time even if you leave the template page. On this page you can follow the status as well as debug any potential errors during deployment.
                <br />
                <br />
                Happy deploying! 🚀
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/Qy3boTpDX5YA0lbD7tGTK_screenshot-2022-10-18-at-130343.png?auto=format&ixlib=react-9.1.1&w=1159&h=781&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#f8cJ1"
        },
        {
            title: "🪲 Improvements and fixes",
            date: "Oct 14, 2022",
            description: <ul>
                <li>Show commit details next to each resource and app</li>
                <li>Improve creating new EKS cluster flow when deploying app</li>
                <li>Show Kubernetes pod events for apps before the app logs become available</li>
                <li>Added welcome page for better user onboarding</li>
            </ul>,
            link: "https://docs.microtica.com/changelog#ec7EG"
        },
        {
            title: "⚡ Track your plan usage in real-time",
            date: "Oct 7, 2022",
            description: <>
                With this update, you can now track your environment, application and build minutes usage for a project in real-time.
                <br />
                <br />
                You can find the usage statistics under <i>Project Settings</i> &#8594; <i>Plan usage</i>.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/AQOv3anTGYgxCKT_BbY7Q_screenshot-2022-10-10-at-131923.png?auto=format&ixlib=react-9.1.1&w=2294&h=952&dpr=2&q=50",
            link: "https://docs.microtica.com/changelog#xuKVv"
        },
        {
            title: "⚡ Deployment summary - review the configuration before deploying an App",
            date: "Sep 30, 2022",
            description: <>
                Now when deploying an app from a template, you will see a summary of the steps that will take in place. Here you can review the Git configuration, the environment in which the app will be deployed and on which cluster/AWS account the resources will be provisioned.
                <br />
                <br />
                Happy deploying! 🚀
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/lJOztTZWulZcAmO--D-Wq_screenshot-2022-09-30-at-124447.png?auto=format&ixlib=react-9.1.1&w=775&h=578&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#xuKVv"
        },
        {
            title: "🪲 Improvements and fixes",
            date: "Sep 29, 2022",
            description: <ul>
                <li>Fixed user with read-only access was not able to see the active pipeline builds</li>
                <li>Improve "Choose where to deploy" template step with better cluster selection logic</li>
                <li>After app deployment from template, navigate users to the app being deployed instead of apps list</li>
            </ul>,
            link: "https://docs.microtica.com/changelog#-2Obt"
        },
        {
            title: "📢 Introducing Framework Detection: deploy your apps much faster",
            date: "Sep 23, 2022",
            description: "You no longer have to have a Dockerfile file present in your repository to be able to deploy your existing applications. We now auto-detect your app framework and set a default configuration for building, packaging and deploying your app.",
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/7PGuYa0ggM6P8I7Pab3o2_image.png?auto=format&ixlib=react-9.1.1&w=1177&h=598&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#xqZ89"
        },
        {
            title: "⚡ Save massive a amount of time finding and fixing your failed deployments",
            date: "Sep 16, 2022",
            description: <>
                We as developers usually don't really pay attention when deployments go smoothly until something goes wrong. In these cases we have to go deeper and analyse where the problem occur and understand the root cause so we can quickly resolve it.
                <br />
                <br />
                With Deployments we are addressing this need to quickly and reliable understand what's going on with your current and past deployments.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/fUUpwdGDOwaOesm7HfaR9_image.png?auto=format&ixlib=react-9.1.1&w=1166&h=629&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#N7oFV"
        },
        {
            title: "🪲 Improvements and fixes",
            date: "Sep 13, 2022",
            description: <ul>
                <li>When removing an AWS account, we now unlink the AWS account from the environments</li>
                <li>Fixed template execution logs not appear if execution failed</li>
                <li>Added a possibility to create additional cluster in templates page</li>
                <li>Fixed users unable to create new repo if the Git account has no other repos</li>
                <li>Removed 'Cancel Plan' button in Billing for FREE plan projects</li>
                <li>Add quick navigation button to logs in app overview page</li>

            </ul>,
            link: "https://docs.microtica.com/changelog#jFz86"
        },
        {
            title: "⚡ Automated validation of your Git webhooks permissions immediately in the first template step",
            date: "Sep 9, 2022",
            description: <>
                When deploying apps using templates, Microtica adds a Webhook to your repository for continuous build and deploy. Since each Git provider requires a specific minimum role to create a webhook, we are now making it immediately visible when the user has no sufficient rights to create a webhook.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/lbpqtdwdS8RqsU_zzLntb_image.png?auto=format&ixlib=react-9.1.1&w=1252&h=386&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#GW9oz"
        },
        {
            title: "⚡ Create Kubernetes cluster without leaving the app deployment steps",
            date: "Sep 2, 2022",
            description: <>
                When deploying an app but you still don't have a Kubernetes cluster, instead of moving to a different page to create the cluster and then go back to the app, you can now provision a new cluster without leaving the app deployment page.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/h0CckC_M3y9dC1FNoiQca_image.png?auto=format&ixlib=react-9.1.1&w=777&h=422&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#jgNaq"
        },
        {
            title: "⚡ Connect your AWS account with one click",
            date: "Aug 26, 2022",
            description: <>
                We are introducing a big improvement when connecting your AWS account by reducing the process to just one click.
                <br />
                <br />
                Now with just one click we initiate the CloudFormation stack that gives Microtica permissions to provision infrastructure on your AWS account.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/CAm0Lk6eAZubKpUfhRbq1_image.png?auto=format&ixlib=react-9.1.1&w=537&h=240&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#6KvDq"
        },
        {
            title: "📢 Introducing AWS CodeCommit integration",
            date: "Aug 19, 2022",
            description: <>
                If you version your source code on CodeCommit, you can now connect the repo and get fully automate deployments directly from the Microtica portal.
                <br />
                <br />
                Happy deploying! 🚀
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/0uET7NYRkWnGe230IEpqZ_image.png?auto=format&ixlib=react-9.1.1&w=846&h=166&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#gBIqP"
        },
    ];

    return (
        <SideDrawer
            title="What's New"
            visible={visible}
            onClose={onClose}
            content={
                changelog.map((log, index) => (
                    <Fragment key={index}>
                        <Heading title={log.title} level={4} />
                        <p>
                            <i>{log.date}</i>
                        </p>
                        <p>
                            {log.description}
                        </p>
                        {
                            log.imageUrl &&
                            <Image width={"100%"} src={log.imageUrl} />
                        }
                        <p>
                            <br />
                            Read more in the <a href={log.link} target="_blank" rel="noreferrer">Changelog</a>
                        </p>
                        <Divider />
                    </Fragment>
                ))
            }
        />
    )
}

export default ChangelogDrawer;